import { createContext, useContext, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import type {
  AberturaPlanta,
  CambioRack,
  CaminoPlanta,
  MuellePlanta,
  NavePlanta,
  ParedPlanta,
  Planta as DatosPlanta,
  RackPlanta,
  TipoRack,
} from '../../api';
import { Icono, Numero } from '../../components/ui';
import { abrir, CURVA, desplegar, fila, pop, resorte, revelarDesde, suave } from '../../components/movimiento';
import {
  altoMinimo,
  botonPrimario,
  botonSecundario,
  c,
  campo,
  chip,
  colorAbertura,
  colorCamino,
  etiquetaCampo,
  mono,
  nota,
  tarjeta,
} from '../../theme';

/**
 * El acordeón del panel lateral: una sección abierta por vez, identificada por
 * el título de su `Bloque`. Va por contexto y no por props de cada formulario
 * porque el título lo dibuja `Bloque`, adentro de ellos: así Planta decide qué
 * está abierto sin que los ocho formularios tengan que enterarse. Fuera de un
 * `Plegable` (el `PanelRack`), `Bloque` sigue siendo la tarjeta de siempre.
 */
type Acordeon = {
  abierta: string | null;
  /** Cuántos elementos tiene cada sección, por título; sin entrada no muestra número. */
  cuentas: Record<string, number>;
  onAlternar: (titulo: string) => void;
};
const ContextoPlegable = createContext<Acordeon | null>(null);

export function Plegable({ children, ...acordeon }: Acordeon & { children: React.ReactNode }) {
  return <ContextoPlegable.Provider value={acordeon}>{children}</ContextoPlegable.Provider>;
}

/**
 * Una fila de las listas del panel (naves, paredes, sectores…). Entra
 * escalonada, sale hacia el costado y las demás se corren a su lugar nuevo.
 * Va adentro de un `<AnimatePresence mode="popLayout">` cuyo contenedor tenga
 * `position: relative`.
 *
 * `layoutDependency` en la cantidad de filas: el reacomodo se mide sólo
 * cuando se agrega o se quita una. Sin eso se mediría en cada render, y al
 * abrir o cerrar una sección del acordeón —que se pliega hasta alto cero— las
 * filas saltarían desde una caja de alto cero.
 */
function FilaLista({
  i,
  filas,
  style,
  children,
}: {
  i: number;
  filas: number;
  style?: React.CSSProperties;
  children: React.ReactNode;
}) {
  const f = fila(i);
  return (
    <motion.div {...f} layout="position" layoutDependency={filas} transition={{ ...f.transition, layout: resorte }} style={style}>
      {children}
    </motion.div>
  );
}

/** El rótulo del botón de dibujar: al cambiar, uno sale y el otro entra, no se reemplaza de golpe. */
function TextoDibujar({ dibujando }: { dibujando: boolean }) {
  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.span
        key={dibujando ? 'cancelar' : 'dibujar'}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.16 }}
        style={{ display: 'inline-block' }}
      >
        {dibujando ? 'Cancelar dibujo (Esc)' : 'Dibujar en el plano'}
      </motion.span>
    </AnimatePresence>
  );
}

/** Una nota que aparece debajo de algo (el modo dibujo, un rack que se sale): se despliega en su lugar. */
function NotaQueAparece({ visible, style, children }: { visible: boolean; style: React.CSSProperties; children: React.ReactNode }) {
  return (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.div key="nota" {...desplegar}>
          <div style={style}>{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function Bloque({ titulo, accion, children }: { titulo: string; accion?: React.ReactNode; children: React.ReactNode }) {
  const acordeon = useContext(ContextoPlegable);
  if (!acordeon) {
    return (
      <div style={{ ...tarjeta, padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: c.texto }}>{titulo}</span>
          {accion}
        </div>
        {children}
      </div>
    );
  }
  const abierto = acordeon.abierta === titulo;
  const cuenta = acordeon.cuentas[titulo];
  return (
    <div style={{ ...tarjeta, padding: 0, display: 'flex', flexDirection: 'column' }}>
      <button
        type="button"
        onClick={() => acordeon.onAlternar(titulo)}
        aria-expanded={abierto}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '14px 16px',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          fontSize: 15,
          fontWeight: 600,
          color: c.texto,
        }}
      >
        <span style={{ flex: 1 }}>{titulo}</span>
        {cuenta !== undefined && (
          <span style={{ fontFamily: mono, fontSize: 12.5, fontWeight: 500, color: c.texto3 }}>
            <Numero valor={cuenta} />
          </span>
        )}
        {/* Una sola flecha que gira, en vez de cambiar un ícono por otro. */}
        <motion.span
          initial={false}
          animate={{ rotate: abierto ? 180 : 0 }}
          transition={suave}
          style={{ display: 'grid', placeItems: 'center' }}
        >
          <Icono nombre="expand_more" size={20} color={c.texto3} />
        </motion.span>
      </button>
      {/* Cerrada se esconde y no se desmonta: los formularios tienen estado
          propio (lo tipeado a medias) que no tiene por qué perderse al mirar
          otra sección. Por eso no va con `AnimatePresence`.

          El alto se anima con CSS (la fila de la grilla de `0fr` a `1fr`) y no
          con Motion: Motion animaba bien el cierre, pero al abrir medía el
          alto `auto` mientras la sección seguía en `display: none` y la
          desplegaba de golpe, con la opacidad saltando al final. Cerrada
          queda en `visibility: hidden`, que la saca del orden de tabulación
          igual que el `display: none` de antes; la transición de `visibility`
          hace que al cerrar recién se oculte cuando terminó de plegarse. */}
      <div
        style={{
          display: 'grid',
          gridTemplateRows: abierto ? '1fr' : '0fr',
          opacity: abierto ? 1 : 0,
          visibility: abierto ? 'visible' : 'hidden',
          transition: `grid-template-rows ${suave.duration}s cubic-bezier(${CURVA.join(',')}), opacity .25s, visibility ${suave.duration}s`,
        }}
      >
        <div style={{ overflow: 'hidden', minHeight: 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 16px 16px', position: 'relative' }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

const chico = { ...campo, height: 38, fontSize: 13 };

/**
 * El predio no tiene medidas propias: son la caja que envuelve a sus naves, y
 * las calcula el backend. Lo único que se configura acá es su nombre, que es
 * el que aparece encabezando el mapa.
 */
export function FormularioPredio({
  valores,
  onCambiar,
  onGuardar,
}: {
  valores: { nombre: string };
  onCambiar: (v: { nombre: string }) => void;
  onGuardar: () => void;
}) {
  return (
    <Bloque titulo="El predio">
      <label style={etiquetaCampo}>
        Nombre
        <input value={valores.nombre} onChange={(e) => onCambiar({ nombre: e.target.value })} style={chico} />
      </label>
      <p style={nota}>
        Las medidas del predio salen solas: son la caja que envuelve a las naves. Las de cada nave se editan más abajo.
      </p>
      <button type="button" onClick={onGuardar} className="max-escritorio:justify-center" style={{ ...botonSecundario, minHeight: altoMinimo(40) }}>
        Guardar el nombre
      </button>
    </Bloque>
  );
}

/**
 * Valor del selector para «en el predio, afuera de las naves». Tiene que ser
 * un valor propio y no la cadena vacía: el vacío es falsy y significaba «sin
 * elegir todavía», así que cualquier `valor || primeraNave` lo repisaba solo
 * y el camino terminaba guardado adentro de una nave sin que nadie lo pidiera.
 */
export const EN_EL_PREDIO = 'predio';

/**
 * En qué nave se crea lo que se está por agregar. El camino es el único que
 * puede quedar afuera de todas —la playa de maniobras entre las naves—, así
 * que la opción del predio se pide con `conPredio`.
 */
function CampoNave({
  naves,
  valor,
  onCambiar,
  conPredio = false,
}: {
  naves: NavePlanta[];
  valor: string;
  onCambiar: (nave: string) => void;
  conPredio?: boolean;
}) {
  return (
    <label style={etiquetaCampo}>
      En qué nave
      <select value={valor} onChange={(e) => onCambiar(e.target.value)} style={chico}>
        {naves.map((n) => (
          <option key={n.id} value={String(n.id)}>
            {n.nombre}
          </option>
        ))}
        {/* Sólo el camino puede estar afuera: la playa de maniobras. */}
        {conPredio && <option value={EN_EL_PREDIO}>En el predio (afuera de las naves)</option>}
      </select>
    </label>
  );
}

/**
 * Alta, baja y medidas de las naves del predio. Se dibuja el rectángulo sobre
 * el plano y la nave queda hecha ahí; arrastrándola después se engancha al
 * borde de la de al lado, que es como quedan las dos pegadas.
 */
export function FormularioNave({
  naves,
  valores,
  fallas,
  onCambiar,
  onBorrar,
  onRenombrar,
  onMedidas,
  dibujando,
  onDibujar,
}: {
  naves: NavePlanta[];
  valores: { nombre: string; alto: string };
  /** Cuántos pedidos rechazó el backend: fuerza el remonte de las filas cuando cambia. */
  fallas: number;
  onCambiar: (v: { nombre: string; alto: string }) => void;
  onBorrar: (n: NavePlanta) => void;
  onRenombrar: (id: number, nombre: string) => void;
  onMedidas: (id: number, medidas: { ancho: number; largo: number; alto: number }) => void;
  dibujando: boolean;
  onDibujar: () => void;
}) {
  return (
    <Bloque titulo="Naves">
      <AnimatePresence mode="popLayout" initial={false}>
      {naves.map((n, i) => (
        <FilaLista key={n.id} i={i} filas={naves.length}>
        <div key={fallas} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              defaultValue={n.nombre}
              onBlur={(e) => {
                const nombre = e.target.value.trim();
                if (nombre && nombre !== n.nombre) onRenombrar(n.id, nombre);
              }}
              aria-label={`Nombre de ${n.nombre}`}
              style={{ ...chico, flex: 1, minWidth: 0 }}
            />
            <button
              type="button"
              onClick={() => onBorrar(n)}
              disabled={!n.vacia}
              title={
                n.vacia
                  ? `Borrar ${n.nombre}`
                  : `${n.nombre} tiene ${n.sectores} sector(es), ${n.muelles} muelle(s), ${n.caminos} camino(s), ${n.paredes} pared(es) y ${n.aberturas} abertura(s) adentro.`
              }
              aria-label={`Borrar ${n.nombre}`}
              style={{
                ...botonSecundario,
                minHeight: altoMinimo(38),
                padding: '0 10px',
                opacity: n.vacia ? 1 : 0.4,
                cursor: n.vacia ? 'pointer' : 'not-allowed',
              }}
            >
              <Icono nombre="delete" />
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {(
              [
                ['ancho', n.ancho],
                ['largo', n.largo],
                ['alto', n.alto],
              ] as const
            ).map(([campoNave, valor]) => (
              <label key={campoNave} style={etiquetaCampo}>
                {campoNave[0]!.toUpperCase() + campoNave.slice(1)} (m)
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.5"
                  defaultValue={String(valor)}
                  onBlur={(e) => {
                    const v = Number(e.target.value);
                    if (!Number.isFinite(v) || v === valor) return;
                    onMedidas(n.id, {
                      ancho: campoNave === 'ancho' ? v : n.ancho,
                      largo: campoNave === 'largo' ? v : n.largo,
                      alto: campoNave === 'alto' ? v : n.alto,
                    });
                  }}
                  style={chico}
                />
              </label>
            ))}
          </div>

          {/* Encoger una nave no se bloquea: se avisa, como ya se hace con los
              racks que se salen de la zona de su sector. */}
          <NotaQueAparece visible={n.racksFuera > 0} style={{ ...nota, color: c.ambar }}>
            {n.racksFuera === 1 ? 'Un rack se sale' : `${n.racksFuera} racks se salen`} de esta nave.
          </NotaQueAparece>
        </div>
        </FilaLista>
      ))}
      </AnimatePresence>

      <p style={nota}>
        Marcá el rectángulo sobre el plano y la nave queda hecha ahí. Arrastrándola se engancha al borde de la de al
        lado, para que queden pegadas sin ranura. El predio no se configura: es la caja que envuelve a las naves.
      </p>

      <label style={etiquetaCampo}>
        Nombre de la nueva
        <input
          value={valores.nombre}
          onChange={(e) => onCambiar({ ...valores, nombre: e.target.value })}
          placeholder={`Depósito ${naves.length + 1}`}
          style={chico}
        />
      </label>

      <label style={etiquetaCampo}>
        Alto de la nueva (m)
        <input
          type="number"
          inputMode="decimal"
          step="0.5"
          value={valores.alto}
          onChange={(e) => onCambiar({ ...valores, alto: e.target.value })}
          style={chico}
        />
      </label>

      <button
        type="button"
        onClick={onDibujar}
        className="max-escritorio:justify-center" style={{ ...(dibujando ? botonPrimario : botonSecundario), minHeight: altoMinimo(40) }}
      >
        <TextoDibujar dibujando={dibujando} />
      </button>
    </Bloque>
  );
}

/**
 * Tabiques interiores: se traza un segmento sobre el plano —se endereza solo
 * al eje dominante— y queda una pared en su espesor real. No hay «agregar sin
 * dibujar» como en el sector: la pared es sus dos puntos, así que sin trazo no
 * hay nada que guardar. El espesor tampoco se edita acá: sale del valor por
 * defecto del backend, y lo único que se ajusta después es el largo, el
 * material y el alto de cada una.
 */
export function FormularioPared({
  valores,
  paredes,
  naves,
  materiales,
  fallas,
  onCambiar,
  onLargo,
  onMaterial,
  onAlto,
  onBorrar,
  dibujando,
  onDibujar,
}: {
  valores: { material: string; alto: string };
  paredes: ParedPlanta[];
  naves: NavePlanta[];
  materiales: { id: string; nombre: string }[];
  /** Cuántos pedidos rechazó el backend: fuerza el remonte de las filas cuando cambia. */
  fallas: number;
  onCambiar: (v: { material: string; alto: string }) => void;
  onLargo: (p: ParedPlanta, largo: number) => void;
  onMaterial: (id: number, material: string) => void;
  onAlto: (id: number, alto: number | null) => void;
  onBorrar: (id: number) => void;
  dibujando: boolean;
  onDibujar: () => void;
}) {
  const naveNombre = (id: number) => naves.find((n) => n.id === id)?.nombre ?? `Nave ${id}`;

  return (
    <Bloque titulo="Paredes">
      {paredes.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, position: 'relative' }}>
          <AnimatePresence mode="popLayout">
          {paredes.map((p, i) => (
            <FilaLista key={p.id} i={i} filas={paredes.length}>
            <div
              key={fallas}
              style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 10, borderBottom: `1px solid ${c.borde}` }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 12, color: c.texto3, flex: 1, minWidth: 0 }}>{naveNombre(p.nave)}</span>
                <button
                  type="button"
                  onClick={() => onBorrar(p.id)}
                  aria-label={`Borrar la pared de ${p.largo} m en ${naveNombre(p.nave)}`}
                  style={{
                    height: 26,
                    width: 26,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 8,
                    border: `1px solid ${c.borde}`,
                    background: c.panel,
                    cursor: 'pointer',
                  }}
                >
                  <Icono nombre="close" size={14} color={c.texto3} />
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <label style={etiquetaCampo}>
                  Largo (m)
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    min="0.5"
                    defaultValue={p.largo}
                    aria-label={`Largo de la pared en ${naveNombre(p.nave)}`}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v > 0 && v !== p.largo) onLargo(p, v);
                      else e.target.value = String(p.largo);
                    }}
                    style={chico}
                  />
                </label>
                <label style={etiquetaCampo}>
                  Material
                  <select
                    value={p.material}
                    onChange={(e) => onMaterial(p.id, e.target.value)}
                    style={{ ...chico, padding: '0 6px' }}
                  >
                    {materiales.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.nombre}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={etiquetaCampo}>
                  Alto (m)
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    min="0"
                    defaultValue={p.alto ?? ''}
                    placeholder="Techo"
                    title="Vacío es hasta el techo de la nave"
                    aria-label={`Alto de la pared en ${naveNombre(p.nave)}`}
                    onBlur={(e) => {
                      const raw = e.target.value.trim();
                      const v = raw === '' ? null : Number(raw);
                      if (v === null ? p.alto !== null : Number.isFinite(v) && v !== p.alto) onAlto(p.id, v);
                    }}
                    style={chico}
                  />
                </label>
              </div>
            </div>
            </FilaLista>
          ))}
          </AnimatePresence>
        </div>
      )}

      <label style={etiquetaCampo}>
        Material de la nueva
        <select
          value={valores.material}
          onChange={(e) => onCambiar({ ...valores, material: e.target.value })}
          style={chico}
        >
          {materiales.map((m) => (
            <option key={m.id} value={m.id}>
              {m.nombre}
            </option>
          ))}
        </select>
      </label>

      <label style={etiquetaCampo}>
        Alto de la nueva (m)
        <input
          type="number"
          inputMode="decimal"
          step="0.5"
          min="0"
          value={valores.alto}
          onChange={(e) => onCambiar({ ...valores, alto: e.target.value })}
          placeholder="Vacío = hasta el techo"
          style={chico}
        />
      </label>

      <p style={nota}>
        Dejar el alto vacío significa que la pared llega hasta el techo de su nave. Marcá el trazo sobre el plano y
        se endereza solo al eje más parecido: nada de tabiques en diagonal.
      </p>

      <button
        type="button"
        onClick={onDibujar}
        className="max-escritorio:justify-center" style={{ ...(dibujando ? botonPrimario : botonSecundario), minHeight: altoMinimo(40) }}
      >
        <TextoDibujar dibujando={dibujando} />
      </button>
    </Bloque>
  );
}

/** Lo único que se edita de una abertura ya colocada: nace con las medidas
 * por defecto de su tipo y de ahí en más se ajusta acá, no arrastrando -a
 * diferencia de un rack o un muelle, una abertura vive pegada a su muro y
 * moverla es cambiarle el offset a lo largo de él, no soltarla en otro lado. */
type CambioAbertura = Partial<{
  offset: number;
  ancho: number;
  alto: number;
  antepecho: number;
  tipo: string;
  nombre: string;
}>;

/**
 * Portones, puertas peatonales, ventanales y el paso entre naves: la
 * herramienta gemela de la pared. Se elige el tipo, se hace clic sobre un
 * muro del plano, y la abertura nace ahí con el ancho, el alto y el
 * antepecho por defecto de ese tipo -el ventanal es el único con antepecho,
 * porque es el único que no llega al piso-. Lo que se pisa o se sale del
 * muro lo rechaza el backend, con un motivo ya redactado para un operario.
 */
export function FormularioAbertura({
  valores,
  aberturas,
  naves,
  paredes,
  tipos,
  fallas,
  onCambiar,
  onEditar,
  onBorrar,
  dibujando,
  onDibujar,
}: {
  valores: { tipo: string; nombre: string };
  aberturas: AberturaPlanta[];
  naves: NavePlanta[];
  paredes: ParedPlanta[];
  tipos: { id: string; nombre: string; anchoM: number; altoM: number; antepechoM: number }[];
  /** Cuántos pedidos rechazó el backend: fuerza el remonte de las filas cuando cambia. */
  fallas: number;
  onCambiar: (v: { tipo: string; nombre: string }) => void;
  onEditar: (id: number, cambio: CambioAbertura) => void;
  onBorrar: (id: number) => void;
  dibujando: boolean;
  onDibujar: () => void;
}) {
  const naveNombre = (id: number) => naves.find((n) => n.id === id)?.nombre ?? `Nave ${id}`;
  const nombreDeTipo = (id: string) => tipos.find((t) => t.id === id)?.nombre ?? id;

  /**
   * El largo del muro que ocupa, para saber si el offset y el ancho todavía
   * entran. No hace falta duplicar `ladoDeNave`: alcanza con la medida de la
   * nave del lado que corresponde, o con el largo ya calculado de la pared.
   */
  const largoDelMuro = (a: AberturaPlanta): number | null => {
    if (a.pared !== null) return paredes.find((p) => p.id === a.pared)?.largo ?? null;
    const nave = naves.find((n) => n.id === a.nave);
    if (!nave || !a.lado) return null;
    return a.lado === 'norte' || a.lado === 'sur' ? nave.ancho : nave.largo;
  };

  /**
   * ¿Se salió del muro? Es la misma cuenta que hace `PlanoEditable` para
   * decidir si la marca queda de largo cero -invisible en el plano, aunque
   * siga en la lista y en el 3D-. El clamp de ahí está bien: lo que faltaba
   * era decirlo, no dejar que el editor calle el problema.
   */
  const fueraDelMuro = (a: AberturaPlanta): boolean => {
    const largo = largoDelMuro(a);
    if (largo === null) return false;
    const desde = Math.max(0, Math.min(largo, a.offset));
    const hasta = Math.max(0, Math.min(largo, a.offset + a.ancho));
    return hasta <= desde;
  };

  return (
    <Bloque titulo="Aberturas">
      {aberturas.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, position: 'relative' }}>
          <AnimatePresence mode="popLayout">
          {aberturas.map((a, i) => (
            <FilaLista key={a.id} i={i} filas={aberturas.length}>
            <div
              // `a.tipo` entra en la key además de `fallas`: cambiar el tipo
              // también cambia el ancho, el alto y el antepecho por detrás
              // (ver el `onChange` de abajo), y esos tres campos son
              // `defaultValue` + `onBlur` -sin `a.tipo` en la key, la fila
              // seguiría montada con los valores viejos aunque el pedido
              // hubiera salido bien.
              key={`${a.id}-${a.tipo}-${fallas}`}
              style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingBottom: 10, borderBottom: `1px solid ${c.borde}` }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span
                  title={nombreDeTipo(a.tipo)}
                  style={{ width: 10, height: 10, borderRadius: 3, flexShrink: 0, background: colorAbertura(a.tipo) }}
                />
                <span style={{ fontSize: 12, color: c.texto3, flex: 1, minWidth: 0 }}>
                  {naveNombre(a.nave)} · {nombreDeTipo(a.tipo)}
                </span>
                <button
                  type="button"
                  onClick={() => onBorrar(a.id)}
                  aria-label={`Borrar ${nombreDeTipo(a.tipo)} en ${naveNombre(a.nave)}`}
                  style={{
                    height: 26,
                    width: 26,
                    display: 'grid',
                    placeItems: 'center',
                    borderRadius: 8,
                    border: `1px solid ${c.borde}`,
                    background: c.panel,
                    cursor: 'pointer',
                  }}
                >
                  <Icono nombre="close" size={14} color={c.texto3} />
                </button>
              </div>

              <input
                type="text"
                defaultValue={a.nombre}
                placeholder="Sin nombre"
                aria-label={`Nombre de ${nombreDeTipo(a.tipo)} en ${naveNombre(a.nave)}`}
                onBlur={(e) => {
                  const nombre = e.target.value.trim();
                  if (nombre !== a.nombre) onEditar(a.id, { nombre });
                }}
                style={chico}
              />

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <label style={etiquetaCampo}>
                  Tipo
                  <select
                    value={a.tipo}
                    onChange={(e) => {
                      const nuevoTipo = e.target.value;
                      // Cambiar el tipo arrastra las medidas por defecto de
                      // ese tipo: si no, un ventanal pasado a «Portón» queda
                      // con el ancho y el antepecho de un ventanal -3×1,5 m
                      // flotando a 4 m del piso-, y la fila dice Portón sin
                      // que el muro se abra. El backend conserva lo que había
                      // si no se le manda nada nuevo, así que hay que
                      // pedírselo con las medidas del tipo elegido.
                      const info = tipos.find((t) => t.id === nuevoTipo);
                      onEditar(
                        a.id,
                        info
                          ? { tipo: nuevoTipo, ancho: info.anchoM, alto: info.altoM, antepecho: info.antepechoM }
                          : { tipo: nuevoTipo },
                      );
                    }}
                    style={{ ...chico, padding: '0 6px' }}
                  >
                    {tipos.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.nombre}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={etiquetaCampo}>
                  Offset (m)
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.5"
                    min="0"
                    defaultValue={a.offset}
                    aria-label={`Offset de ${nombreDeTipo(a.tipo)} en ${naveNombre(a.nave)}`}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v !== a.offset) onEditar(a.id, { offset: v });
                      else e.target.value = String(a.offset);
                    }}
                    style={chico}
                  />
                </label>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <label style={etiquetaCampo}>
                  Ancho (m)
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0.1"
                    defaultValue={a.ancho}
                    aria-label={`Ancho de ${nombreDeTipo(a.tipo)} en ${naveNombre(a.nave)}`}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v > 0 && v !== a.ancho) onEditar(a.id, { ancho: v });
                      else e.target.value = String(a.ancho);
                    }}
                    style={chico}
                  />
                </label>
                <label style={etiquetaCampo}>
                  Alto (m)
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0.1"
                    defaultValue={a.alto}
                    aria-label={`Alto de ${nombreDeTipo(a.tipo)} en ${naveNombre(a.nave)}`}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v > 0 && v !== a.alto) onEditar(a.id, { alto: v });
                      else e.target.value = String(a.alto);
                    }}
                    style={chico}
                  />
                </label>
                <label style={etiquetaCampo}>
                  Antepecho (m)
                  <input
                    type="number"
                    inputMode="decimal"
                    step="0.1"
                    min="0"
                    defaultValue={a.antepecho}
                    title="A qué altura arranca: 0 es que llega al piso"
                    aria-label={`Antepecho de ${nombreDeTipo(a.tipo)} en ${naveNombre(a.nave)}`}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (Number.isFinite(v) && v >= 0 && v !== a.antepecho) onEditar(a.id, { antepecho: v });
                      else e.target.value = String(a.antepecho);
                    }}
                    style={chico}
                  />
                </label>
              </div>

              {/* El clamp de `PlanoEditable` deja la marca de largo cero -
                  invisible en el plano- sin decir nada: acá se lo dice. */}
              <NotaQueAparece visible={fueraDelMuro(a)} style={{ ...nota, color: c.rojo }}>
                Se salió de su muro: no se ve en el plano. Ajustá el offset o el ancho.
              </NotaQueAparece>
            </div>
            </FilaLista>
          ))}
          </AnimatePresence>
        </div>
      )}

      <label style={etiquetaCampo}>
        Tipo de la nueva
        <select
          value={valores.tipo}
          onChange={(e) => onCambiar({ ...valores, tipo: e.target.value })}
          style={chico}
        >
          {tipos.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </select>
      </label>

      <label style={etiquetaCampo}>
        Nombre (opcional)
        <input
          value={valores.nombre}
          onChange={(e) => onCambiar({ ...valores, nombre: e.target.value })}
          style={chico}
        />
      </label>

      <p style={nota}>
        Hacé clic sobre un muro del plano y la abertura nace ahí, con el ancho y el alto por defecto del tipo. El
        offset, el ancho, el alto y el antepecho se ajustan después, acá arriba.
      </p>

      <button
        type="button"
        onClick={onDibujar}
        className="max-escritorio:justify-center" style={{ ...(dibujando ? botonPrimario : botonSecundario), minHeight: altoMinimo(40) }}
      >
        <TextoDibujar dibujando={dibujando} />
      </button>
    </Bloque>
  );
}

export function FormularioSector({
  valores,
  sectores,
  naves,
  colores,
  colorDe,
  onCambiar,
  onCrear,
  onBorrar,
  onColor,
  onRenombrar,
  dibujando,
  onDibujar,
}: {
  valores: { id: string; nombre: string; uso: string; nave: string };
  sectores: DatosPlanta['sectores'];
  naves: NavePlanta[];
  colores: string[];
  colorDe: (id: string) => string;
  onCambiar: (v: { id: string; nombre: string; uso: string; nave: string }) => void;
  onCrear: () => void;
  onBorrar: (id: string) => void;
  onColor: (id: string, color: string) => void;
  onRenombrar: (id: string, nuevo: string) => void;
  dibujando: boolean;
  onDibujar: () => void;
}) {
  // Qué sector se está renombrando. Uno por vez y con un clic explícito para
  // abrirlo: cambiarle la letra reescribe el código de cada rack y de cada
  // posición del sector, no es un campo más de la fila.
  const [renombrando, setRenombrando] = useState<string | null>(null);
  const [letraNueva, setLetraNueva] = useState('');

  const abrirRenombre = (id: string) => {
    setRenombrando((r) => (r === id ? null : id));
    setLetraNueva('');
  };

  return (
    <Bloque titulo="Sectores">
      {sectores.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
          <AnimatePresence mode="popLayout">
          {sectores.map((s, i) => (
            <FilaLista key={s.id} i={i} filas={sectores.length} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              {/* El color se cambia acá mismo: es lo que hace que un sector se
                  encuentre de un vistazo en el plano. */}
              <label
                title={`Color de ${s.nombre}`}
                style={{
                  width: 14,
                  height: 14,
                  borderRadius: 4,
                  flex: '0 0 14px',
                  background: colorDe(s.id),
                  cursor: 'pointer',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <select
                  value={s.color ?? ''}
                  onChange={(e) => onColor(s.id, e.target.value)}
                  aria-label={`Color de ${s.nombre}`}
                  style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
                >
                  {colores.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </label>
              <span style={{ fontWeight: 600, color: c.texto }}>{s.id}</span>
              <span style={{ flex: 1, minWidth: 0, color: c.texto2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {s.nombre} · {s.racks} racks
                {s.racksFuera > 0 && (
                  <span style={{ color: c.ambar }}> · {s.racksFuera} fuera de la zona</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => abrirRenombre(s.id)}
                aria-expanded={renombrando === s.id}
                aria-label={`Cambiarle la letra al sector ${s.id}`}
                title="Cambiarle la letra: se la cambia también a sus racks y posiciones"
                style={{ border: 0, background: 'transparent', color: c.texto3, cursor: 'pointer', display: 'grid' }}
              >
                <Icono nombre="edit" size={16} />
              </button>
              <button
                type="button"
                onClick={() => onBorrar(s.id)}
                aria-label={`Borrar el sector ${s.id}`}
                style={{ border: 0, background: 'transparent', color: c.rojo, cursor: 'pointer', display: 'grid' }}
              >
                <Icono nombre="delete" size={17} />
              </button>
            </FilaLista>
          ))}
          </AnimatePresence>
        </div>
      )}

      <AnimatePresence initial={false}>
      {renombrando && (
        <motion.div key="renombre" {...desplegar}>
        {/* Nace del lápiz de la fila, que está arriba a la derecha. */}
        <motion.div
          {...revelarDesde('calc(100% - 44px)', '0px')}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: 12,
            borderRadius: 12,
            background: c.fondo,
            border: `1px solid ${c.borde}`,
          }}
        >
          <span style={{ fontSize: 13, fontWeight: 600, color: c.texto }}>
            Cambiarle la letra al sector {renombrando}
          </span>
          {/* Con `wrap`: en el celular los dos botones no entran al lado del
              campo: que bajen a otro renglón antes que apretarse. */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ ...etiquetaCampo, width: 80 }}>
              Letra nueva
              <input
                value={letraNueva}
                onChange={(e) => setLetraNueva(e.target.value.toUpperCase().slice(0, 3))}
                placeholder={renombrando}
                autoComplete="off"
                spellCheck={false}
                style={{ ...chico, fontFamily: mono, textAlign: 'center' }}
              />
            </label>
            <button
              type="button"
              disabled={!letraNueva.trim() || letraNueva.trim() === renombrando}
              onClick={() => {
                onRenombrar(renombrando, letraNueva.trim());
                setRenombrando(null);
                setLetraNueva('');
              }}
              style={{
                ...botonSecundario,
                minHeight: altoMinimo(38),
                opacity: !letraNueva.trim() || letraNueva.trim() === renombrando ? 0.55 : 1,
              }}
            >
              Renombrar
            </button>
            <button
              type="button"
              onClick={() => setRenombrando(null)}
              style={{ ...botonSecundario, minHeight: altoMinimo(38) }}
            >
              Cancelar
            </button>
          </div>
          <div style={nota}>
            {(() => {
              const s = sectores.find((x) => x.id === renombrando);
              const destino = letraNueva.trim() || '?';
              return (
                <>
                  Sus {s?.racks ?? 0} racks y todas sus posiciones pasan a empezar con {destino}:{' '}
                  <span style={{ fontFamily: mono }}>
                    {renombrando}1-01 → {destino}1-01
                  </span>
                  . Los pallets guardados no se mueven: el lugar es el mismo y cambia de nombre. Para intercambiar
                  dos letras hay que pasar una por una libre primero.
                </>
              );
            })()}
          </div>
        </motion.div>
        </motion.div>
      )}
      </AnimatePresence>

      <div style={{ display: 'grid', gridTemplateColumns: '70px 1fr', gap: 8 }}>
        <label style={etiquetaCampo}>
          Letra
          <input
            value={valores.id}
            onChange={(e) => onCambiar({ ...valores, id: e.target.value.toUpperCase().slice(0, 3) })}
            placeholder="A"
            style={{ ...chico, fontFamily: mono, textAlign: 'center' }}
          />
        </label>
        <label style={etiquetaCampo}>
          Nombre
          <input
            value={valores.nombre}
            onChange={(e) => onCambiar({ ...valores, nombre: e.target.value })}
            placeholder="Sector A"
            style={chico}
          />
        </label>
      </div>
      <label style={etiquetaCampo}>
        Uso
        <input
          value={valores.uso}
          onChange={(e) => onCambiar({ ...valores, uso: e.target.value })}
          placeholder="Almacenaje general"
          style={chico}
        />
      </label>
      <CampoNave naves={naves} valor={valores.nave} onCambiar={(nave) => onCambiar({ ...valores, nave })} />
      <button
        type="button"
        onClick={onDibujar}
        aria-pressed={dibujando}
        style={{
          ...botonSecundario,
          minHeight: altoMinimo(40),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          background: dibujando ? c.verdeSuave : c.panel,
          borderColor: dibujando ? c.verde : c.bordeFuerte,
          color: dibujando ? c.verde : c.texto2,
        }}
      >
        <Icono nombre={dibujando ? 'close' : 'draw'} size={17} color={dibujando ? c.verde : c.texto3} />
        <TextoDibujar dibujando={dibujando} />
      </button>

      <NotaQueAparece visible={dibujando} style={nota}>
        Marcá la zona del sector sobre el plano. Si no le ponés letra, se le da la primera libre, y el color sale de
        la paleta sin repetir.
      </NotaQueAparece>

      <button
        type="button"
        onClick={onCrear}
        disabled={!valores.id.trim()}
        className="max-escritorio:justify-center" style={{ ...botonSecundario, minHeight: altoMinimo(40), opacity: valores.id.trim() ? 1 : 0.5 }}
      >
        Agregar sin dibujar
      </button>
    </Bloque>
  );
}

export type ValoresRack = {
  sector: string;
  numero: string;
  columnas: string;
  niveles: string;
  /** Cuántos racks paralelos salen del mismo trazo. */
  cantidad: string;
  /** Pasillo entre la espalda de uno y la del siguiente, en metros. */
  separacion: string;
  /** La estación de fraccionamiento y la zona de piso son de 1 columna y 1 nivel. */
  tipo: TipoRack;
};

const TIPOS_RACK: [TipoRack, string][] = [
  ['rack', 'Rack'],
  ['estacion', 'Estación de fraccionamiento'],
  ['piso', 'Zona de piso (pallets grandes)'],
];

/** Rack, estación o zona de piso. Las dos últimas son siempre de 1 columna y 1 nivel. */
function SelectorTipoRack({ tipo, onElegir }: { tipo: TipoRack; onElegir: (tipo: TipoRack) => void }) {
  return (
    <label style={etiquetaCampo}>
      Tipo
      <select value={tipo} onChange={(e) => onElegir(e.target.value as TipoRack)} style={{ ...chico, padding: '0 10px' }}>
        {TIPOS_RACK.map(([id, nombre]) => (
          <option key={id} value={id}>
            {nombre}
          </option>
        ))}
      </select>
    </label>
  );
}

export function FormularioRack({
  valores,
  sectores,
  onCambiar,
  onCrear,
  dibujando,
  onDibujar,
}: {
  valores: ValoresRack;
  sectores: DatosPlanta['sectores'];
  onCambiar: (v: ValoresRack) => void;
  onCrear: () => void;
  dibujando: boolean;
  onDibujar: () => void;
}) {
  const sinSectores = sectores.length === 0;

  return (
    <Bloque titulo="Agregar rack">
      {sinSectores ? (
        <span style={{ fontSize: 13, color: c.texto2 }}>Primero creá un sector.</span>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 8 }}>
            <label style={etiquetaCampo}>
              Sector
              <select
                value={valores.sector}
                onChange={(e) => onCambiar({ ...valores, sector: e.target.value })}
                style={{ ...chico, padding: '0 10px' }}
              >
                {sectores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.id} · {s.nombre}
                  </option>
                ))}
              </select>
            </label>
            <label style={etiquetaCampo}>
              Nº
              <input
                type="number"
                inputMode="numeric"
                min={1}
                value={valores.numero}
                onChange={(e) => onCambiar({ ...valores, numero: e.target.value })}
                style={chico}
              />
            </label>
          </div>
          <SelectorTipoRack
            tipo={valores.tipo}
            onElegir={(tipo) =>
              onCambiar(tipo === 'rack' ? { ...valores, tipo } : { ...valores, tipo, columnas: '1', niveles: '1' })
            }
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {/* Al dibujar, las posiciones las decide el largo del trazo y este
                campo se ignora: mostrarlo invitaba a cargar un número que
                después no aparecía en el rack. Una estación es siempre de una
                sola columna y un solo nivel, y una zona de piso también: el
                servidor lo fuerza igual, pero acá se lo ve antes de guardar. */}
            <AnimatePresence initial={false}>
            {!dibujando && (
              <motion.label key="posiciones" {...abrir(10)} style={etiquetaCampo}>
                Posiciones
                <input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  disabled={valores.tipo !== 'rack'}
                  value={valores.columnas}
                  onChange={(e) => onCambiar({ ...valores, columnas: e.target.value })}
                  style={{ ...chico, opacity: valores.tipo !== 'rack' ? 0.55 : 1 }}
                />
              </motion.label>
            )}
            </AnimatePresence>
            <label style={etiquetaCampo}>
              Niveles
              <input
                type="number"
                inputMode="numeric"
                min={1}
                disabled={valores.tipo !== 'rack'}
                value={valores.niveles}
                onChange={(e) => onCambiar({ ...valores, niveles: e.target.value })}
                style={{ ...chico, opacity: valores.tipo !== 'rack' ? 0.55 : 1 }}
              />
            </label>
            <label style={etiquetaCampo}>
              Cantidad
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={40}
                value={valores.cantidad}
                onChange={(e) => onCambiar({ ...valores, cantidad: e.target.value })}
                style={chico}
              />
            </label>
            <label style={etiquetaCampo}>
              Pasillo (m)
              <input
                type="number"
                inputMode="decimal"
                min={0}
                step={0.5}
                value={valores.separacion}
                onChange={(e) => onCambiar({ ...valores, separacion: e.target.value })}
                style={chico}
              />
            </label>
          </div>

          <span style={{ fontSize: 12, color: c.texto2 }}>
            {Number(valores.cantidad) > 1
              ? `Salen ${valores.cantidad} racks paralelos numerados desde el ${valores.numero}, con ${valores.separacion || 0} m entre espaldas.`
              : 'Dibujado sobre el plano, el largo del trazo decide cuántas posiciones entran.'}
          </span>

          <button
            type="button"
            onClick={onDibujar}
            className="max-escritorio:justify-center" style={{ ...(dibujando ? botonPrimario : botonSecundario), minHeight: altoMinimo(40) }}
          >
            <TextoDibujar dibujando={dibujando} />
          </button>
          <button type="button" onClick={onCrear} className="max-escritorio:justify-center" style={{ ...botonSecundario, minHeight: altoMinimo(40) }}>
            Crear sin ubicar
          </button>
        </>
      )}
    </Bloque>
  );
}

/**
 * Alta y baja de muelles. Un muelle es una boca de carga: se le da un nombre y
 * su medida, y después se lo arrastra a la pared donde está en la realidad.
 */
export function FormularioMuelle({
  valores,
  muelles,
  naves,
  onCambiar,
  onCrear,
  onBorrar,
  onUbicar,
}: {
  valores: { id: string; ancho: string; largo: string; nave: string };
  muelles: MuellePlanta[];
  naves: NavePlanta[];
  onCambiar: (v: { id: string; ancho: string; largo: string; nave: string }) => void;
  onCrear: () => void;
  onBorrar: (id: string) => void;
  onUbicar: (m: MuellePlanta) => void;
}) {
  return (
    <Bloque titulo="Muelles">

      {muelles.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
          <AnimatePresence mode="popLayout">
          {muelles.map((m, i) => (
            <FilaLista key={m.id} i={i} filas={muelles.length} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontFamily: mono, fontSize: 12.5, color: c.texto, width: 64 }}>{m.id}</span>
              <span style={{ fontSize: 12, color: c.texto3, flex: 1, minWidth: 0 }}>
                {m.ancho} × {m.largo} m
                {m.ubicado ? ` · en ${m.x!.toFixed(1)}, ${m.y!.toFixed(1)}` : ' · sin ubicar'}
              </span>
              <AnimatePresence initial={false}>
              {!m.ubicado && (
                <motion.button
                  key="ubicar"
                  {...pop}
                  type="button"
                  onClick={() => onUbicar(m)}
                  style={{ ...botonSecundario, height: 30, padding: '0 10px', fontSize: 12 }}
                >
                  Ubicar
                </motion.button>
              )}
              </AnimatePresence>
              <button
                type="button"
                onClick={() => onBorrar(m.id)}
                aria-label={`Borrar muelle ${m.id}`}
                style={{
                  height: 30,
                  width: 30,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 8,
                  border: `1px solid ${c.borde}`,
                  background: c.panel,
                  cursor: 'pointer',
                }}
              >
                <Icono nombre="close" size={15} color={c.texto3} />
              </button>
            </FilaLista>
          ))}
          </AnimatePresence>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ ...etiquetaCampo, flex: 1, minWidth: 96 }}>
          Nombre
          <input
            type="text"
            value={valores.id}
            onChange={(e) => onCambiar({ ...valores, id: e.target.value.toUpperCase() })}
            placeholder="M1"
            style={{ ...campo, fontFamily: mono }}
          />
        </label>
        <label style={{ ...etiquetaCampo, width: 84 }}>
          Ancho (m)
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={valores.ancho}
            onChange={(e) => onCambiar({ ...valores, ancho: e.target.value })}
            style={campo}
          />
        </label>
        <label style={{ ...etiquetaCampo, width: 96 }}>
          Fondo (m)
          <input
            type="number"
            inputMode="decimal"
            step="0.1"
            value={valores.largo}
            onChange={(e) => onCambiar({ ...valores, largo: e.target.value })}
            style={campo}
          />
        </label>
      </div>

      <CampoNave naves={naves} valor={valores.nave} onCambiar={(nave) => onCambiar({ ...valores, nave })} />

      <button
        type="button"
        onClick={onCrear}
        disabled={!valores.id.trim()}
        className="max-escritorio:justify-center" style={{ ...botonPrimario, minHeight: altoMinimo(40), opacity: valores.id.trim() ? 1 : 0.6 }}
      >
        Agregar muelle
      </button>
    </Bloque>
  );
}

/**
 * Caminos: las franjas de piso que no guardan nada —pasillos, sendas
 * peatonales, zonas de preparación— pero que hacen que el plano se parezca al
 * depósito en vez de ser todo blanco. Cada tipo se pinta de un color.
 */
export function FormularioCamino({
  valores,
  caminos,
  naves,
  tipos,
  onCambiar,
  onCrear,
  onBorrar,
  onUbicar,
  onRenombrar,
  dibujando,
  onDibujar,
}: {
  valores: { nombre: string; tipo: string; ancho: string; largo: string; nave: string };
  caminos: CaminoPlanta[];
  naves: NavePlanta[];
  tipos: { id: string; nombre: string }[];
  onCambiar: (v: { nombre: string; tipo: string; ancho: string; largo: string; nave: string }) => void;
  onCrear: () => void;
  onBorrar: (id: number, nombre: string) => void;
  onUbicar: (k: CaminoPlanta) => void;
  onRenombrar: (id: number, nombre: string) => void;
  dibujando: boolean;
  onDibujar: () => void;
}) {
  return (
    <Bloque titulo="Caminos y zonas">

      {caminos.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, position: 'relative' }}>
          <AnimatePresence mode="popLayout">
          {caminos.map((k, i) => (
            <FilaLista key={k.id} i={i} filas={caminos.length} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span
                style={{
                  width: 12,
                  height: 12,
                  borderRadius: 3,
                  flexShrink: 0,
                  background: colorCamino(k.tipo).relleno,
                  border: `1px solid ${colorCamino(k.tipo).borde}`,
                }}
              />
              <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                <input
                  type="text"
                  defaultValue={k.nombre}
                  aria-label={`Nombre de ${k.nombre}`}
                  onBlur={(e) => {
                    const nombre = e.target.value.trim();
                    if (nombre && nombre !== k.nombre) onRenombrar(k.id, nombre);
                    else e.target.value = k.nombre;
                  }}
                  style={{
                    border: 0,
                    borderBottom: `1px dotted ${c.bordeFuerte}`,
                    background: 'transparent',
                    padding: '1px 0',
                    fontSize: 12.5,
                    color: c.texto,
                    minWidth: 0,
                  }}
                />
                <span style={{ fontSize: 12, color: c.texto3 }}>
                  {k.ancho} × {k.largo} m{k.ubicado ? '' : ' · sin ubicar'}
                </span>
              </span>
              <AnimatePresence initial={false}>
              {!k.ubicado && (
                <motion.button
                  key="ubicar"
                  {...pop}
                  type="button"
                  onClick={() => onUbicar(k)}
                  style={{ ...botonSecundario, height: 30, padding: '0 10px', fontSize: 12 }}
                >
                  Ubicar
                </motion.button>
              )}
              </AnimatePresence>
              <button
                type="button"
                onClick={() => onBorrar(k.id, k.nombre)}
                aria-label={`Borrar ${k.nombre}`}
                style={{
                  height: 30,
                  width: 30,
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: 8,
                  border: `1px solid ${c.borde}`,
                  background: c.panel,
                  cursor: 'pointer',
                }}
              >
                <Icono nombre="close" size={15} color={c.texto3} />
              </button>
            </FilaLista>
          ))}
          </AnimatePresence>
        </div>
      )}

      <label style={etiquetaCampo}>
        Nombre
        <input
          type="text"
          value={valores.nombre}
          onChange={(e) => onCambiar({ ...valores, nombre: e.target.value })}
          placeholder="Pasillo central"
          style={campo}
        />
      </label>

      <button
        type="button"
        onClick={onDibujar}
        aria-pressed={dibujando}
        style={{
          ...botonSecundario,
          minHeight: altoMinimo(40),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          background: dibujando ? c.verdeSuave : c.panel,
          borderColor: dibujando ? c.verde : c.bordeFuerte,
          color: dibujando ? c.verde : c.texto2,
        }}
      >
        <Icono nombre={dibujando ? 'close' : 'draw'} size={17} color={dibujando ? c.verde : c.texto3} />
        <TextoDibujar dibujando={dibujando} />
      </button>

      <NotaQueAparece visible={dibujando} style={nota}>
        Marcá el rectángulo sobre el plano y el camino queda hecho ahí, con el tipo elegido acá abajo. Si no le
        ponés nombre, se numera solo.
      </NotaQueAparece>

      <label style={etiquetaCampo}>
        Tipo
        <select value={valores.tipo} onChange={(e) => onCambiar({ ...valores, tipo: e.target.value })} style={campo}>
          {tipos.map((t) => (
            <option key={t.id} value={t.id}>
              {t.nombre}
            </option>
          ))}
        </select>
      </label>

      <CampoNave naves={naves} valor={valores.nave} onCambiar={(nave) => onCambiar({ ...valores, nave })} conPredio />

      <div style={{ display: 'flex', gap: 8 }}>
        <label style={{ ...etiquetaCampo, flex: 1 }}>
          Ancho (m)
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            value={valores.ancho}
            onChange={(e) => onCambiar({ ...valores, ancho: e.target.value })}
            style={campo}
          />
        </label>
        <label style={{ ...etiquetaCampo, flex: 1 }}>
          Largo (m)
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            value={valores.largo}
            onChange={(e) => onCambiar({ ...valores, largo: e.target.value })}
            style={campo}
          />
        </label>
      </div>

      <button
        type="button"
        onClick={onCrear}
        disabled={!valores.nombre.trim()}
        className="max-escritorio:justify-center" style={{ ...botonPrimario, minHeight: altoMinimo(40), opacity: valores.nombre.trim() ? 1 : 0.6 }}
      >
        Agregar camino
      </button>
    </Bloque>
  );
}

export function PanelRack({
  rack,
  onCambiar,
  onBorrar,
  onCerrar,
  onImprimir,
}: {
  rack: RackPlanta;
  onCambiar: (cambio: CambioRack) => void;
  onBorrar: () => void;
  onCerrar: () => void;
  onImprimir: () => void;
}) {
  const esEstacion = rack.tipo === 'estacion';

  const num = (etiqueta: string, valor: number, paso: number, clave: keyof CambioRack, sufijo = 'm') => {
    // La estación y la zona de piso son siempre de una columna y un nivel: el
    // campo se deshabilita en vez de esconderse, para que se vea por qué no se
    // puede tocar.
    const bloqueado = rack.tipo !== 'rack' && (clave === 'columnas' || clave === 'niveles');
    return (
      <label style={etiquetaCampo}>
        {etiqueta} {sufijo && `(${sufijo})`}
        <input
          // Sin control: al cambiar de tipo o de valor se remonta, si no
          // quedaba mostrando lo de antes de tildar la estación.
          key={`${rack.tipo}:${valor}`}
          type="number"
          inputMode={Number.isInteger(paso) ? 'numeric' : 'decimal'}
          step={paso}
          disabled={bloqueado}
          defaultValue={bloqueado ? 1 : valor}
          onBlur={(e) => {
            const v = Number(e.target.value);
            if (Number.isFinite(v) && v !== valor) onCambiar({ [clave]: v } as CambioRack);
          }}
          style={{ ...chico, opacity: bloqueado ? 0.55 : 1 }}
        />
      </label>
    );
  };

  // Cerrar va junto al título en el celular y la tablet: al lado del detalle
  // lo partía en dos renglones y quedaba flotando a media altura. En la PC,
  // donde entra, sigue en su lugar de siempre.
  const cerrar = (clase: string) => (
    <button type="button" onClick={onCerrar} aria-label="Cerrar" className={clase} style={{ border: 0, background: 'transparent', cursor: 'pointer' }}>
      <Icono nombre="close" size={18} color={c.texto2} />
    </button>
  );

  return (
    <Bloque titulo={`Rack ${rack.id}`} accion={cerrar('grid escritorio:hidden')}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: c.texto2 }}>
        {esEstacion && <span style={chip('neu')}>Estación</span>}
        {rack.tipo === 'piso' && <span style={chip('neu')}>Zona de piso</span>}
        <span style={{ flex: 1 }}>
          {rack.tipo === 'piso' ? (
            <>
              Área libre, sin lugares fijos · <Numero valor={rack.ocupados} /> pallets
            </>
          ) : (
            <>
              {rack.columnas} posiciones × {rack.niveles} niveles ={' '}
              <strong style={{ color: c.texto }}>
                <Numero valor={rack.lugares} />
              </strong>{' '}
              lugares · <Numero valor={rack.ocupados} /> ocupados
            </>
          )}
        </span>
        {cerrar('hidden escritorio:grid')}
      </div>

      <SelectorTipoRack
        tipo={rack.tipo}
        onElegir={(tipo) => onCambiar(tipo === 'rack' ? { tipo } : { tipo, columnas: 1, niveles: 1 })}
      />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        {num('Posiciones', rack.columnas, 1, 'columnas', '')}
        {num('Niveles', rack.niveles, 1, 'niveles', '')}
        {num('Ancho de posición', rack.anchoPosicion, 0.05, 'anchoPosicion')}
        {num('Profundidad', rack.profundidad, 0.05, 'profundidad')}
        {num('Alto de nivel', rack.altoNivel, 0.05, 'altoNivel')}
      </div>

      <label style={etiquetaCampo}>
        Orientación
        <select
          value={rack.angulo}
          onChange={(e) => onCambiar({ angulo: Number(e.target.value) })}
          style={{ ...chico, padding: '0 10px' }}
        >
          <option value={0}>Horizontal (este–oeste)</option>
          <option value={90}>Vertical (norte–sur)</option>
        </select>
      </label>

      {/* Imprimir va a lo ancho y los otros dos se reparten el renglón de
          abajo: a su ancho quedaban desparejos, y en la PC «Borrar rack» caía
          solo en un renglón. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" onClick={onImprimir} className="basis-full justify-center" style={{ ...botonSecundario, minHeight: altoMinimo(38), fontSize: 13 }}>
          <Icono nombre="print" size={16} color={c.texto3} />
          Imprimir etiquetas de ubicación
        </button>
        <button
          type="button"
          onClick={() => onCambiar({ x: null, y: null })}
          className="flex-1 justify-center"
          style={{ ...botonSecundario, minHeight: altoMinimo(38), fontSize: 13 }}
        >
          Sacar del plano
        </button>
        <button
          type="button"
          onClick={onBorrar}
          disabled={rack.ocupados > 0}
          title={rack.ocupados > 0 ? 'Tiene pallets guardados' : undefined}
          className="flex-1 justify-center"
          style={{
            ...botonSecundario,
            minHeight: altoMinimo(38),
            fontSize: 13,
            color: c.rojo,
            opacity: rack.ocupados > 0 ? 0.5 : 1,
          }}
        >
          Borrar rack
        </button>
      </div>
    </Bloque>
  );
}
